import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import useAuth from '../hooks/useAuth';
import { getMentorProfile, updateMentorProfile } from '../services/mentorApi';

export default function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!user?.mentor_id) return;
    getMentorProfile(user.mentor_id)
      .then((data) => setProfile(data.profile))
      .catch((err) => setError(err?.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [user]);

  const handleToggle = async (field, value) => {
    const next = { ...profile, [field]: value };
    setProfile(next);
    setSaving(true);
    try {
      await updateMentorProfile({
        mentorId: user.mentor_id,
        acceptingHome: next.accepting_home,
        acceptingBusiness: next.accepting_business,
      });
      setSnack({ open: true, message: 'Availability updated.', severity: 'success' });
    } catch (err) {
      setSnack({
        open: true,
        message: err?.response?.data?.error || err.message || 'Failed to save',
        severity: 'error',
      });
      setProfile((prev) => ({ ...prev, [field]: !value })); // revert
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>My Profile</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Your mentor account details and review availability.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 3, maxWidth: 720, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Account</Typography>
        <Stack spacing={2} divider={<Divider />}>
          <Field label="Name" value={user?.name || '—'} />
          <Field label="Email" value={user?.email || '—'} />
          <Field label="Mentor ID" value={user?.mentor_id || '—'} mono />
          <Box>
            <Typography variant="caption" color="text.secondary">Groups</Typography>
            <Box sx={{ mt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(user?.groups || []).map((g) => (
                <Chip key={g} label={g} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, maxWidth: 720 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Review Availability
          {saving && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Toggle off a specialty when you&apos;re not available to review that type of assessment.
          You&apos;ll stop receiving new-assessment emails for it, but any already in your queue stay claimed.
        </Typography>

        <Stack spacing={1} sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <HomeIcon color="action" />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>Home Assessments</Typography>
                <Typography variant="caption" color="text.secondary">Residential client reviews</Typography>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={profile?.accepting_home ?? true}
                  onChange={(e) => handleToggle('accepting_home', e.target.checked)}
                  disabled={saving}
                />
              }
              label={profile?.accepting_home === false ? 'Paused' : 'Accepting'}
              labelPlacement="start"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <BusinessIcon color="action" />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>Business Assessments</Typography>
                <Typography variant="caption" color="text.secondary">SMB client reviews</Typography>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={profile?.accepting_business ?? true}
                  onChange={(e) => handleToggle('accepting_business', e.target.checked)}
                  disabled={saving}
                />
              }
              label={profile?.accepting_business === false ? 'Paused' : 'Accepting'}
              labelPlacement="start"
            />
          </Box>
        </Stack>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body1" sx={{ fontFamily: mono ? 'monospace' : undefined }}>
        {value}
      </Typography>
    </Box>
  );
}
